import { Helper } from "_utils";
import { ISignalAlert } from "components/admin/analysis/strategy-profit/ISignalAlert";
import { TradeSetupFilterOptions } from "components/admin/autotrade/TradeSetupForm";
import { ITradeLivePrice } from "components/admin/autotrade/TradesListPage";
import { PresetDateRange } from "components/admin/autotrade/filters/PresetDateRangeFilter";
import { AnalysisFilters } from "components/common/analysis/analysis.filters";
import { AnalysisInputParams, AutoTradeSecurityTypeEnum, AutoTradeSetupStatusEnum, AutoTradeStatusEnum, DateHelper, DirectionType, IAutoTrade, IAutoTradeIbkrOrder, IAutoTradeSetup, IBKROrderType, IBKRSide, ITradeTemplate, OptionType, SignalDirectionType, SignalRepeatType, StockHelper, TriggerExitTypeEnumHelper } from "predictagram-lib";
import { adminAutoTradeSetupService, adminSignalAlertApiService } from "services/AdminApiService";

export type Comparator = (a: IAutoTrade,b: IAutoTrade, currentDirection: "asc" | "desc") => number
export type TableColumn = {
  name: string | undefined, 
  title: string, 
  comparator?: Comparator | undefined
}

export interface ISortColumn {
  tableColumn: TableColumn,
  direction: "asc" | "desc",
}

export interface IQuantity {
  active: number,
  closed: number,
  total: number,
  other: number,
}

export class TradeModel {

  static compareHelper (a: string | number | null, b: string | number | null, currentDirection: "asc" | "desc")  {

    if ((a === null || a === '') && (b === null || b === '')) {
      return 0;
    }

    if (a === null || a.toString() === "" || a.toString() === "undefined") {
      return 1;
    }

    if (b === null || b.toString() === "" || b.toString() === "undefined") {
      return -1;
    }
 
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b * (currentDirection === "asc" ? 1 : -1)
    }

    const result = a.toString().localeCompare(b.toString(), 'en-US', {numeric: true});
    if (result < 0) {
      return currentDirection === "asc" ? -1 : 1;
    }
  
    if (result > 0) {
      return currentDirection === "asc" ? 1 : -1;
    }
    return 0;  
  }

  static tradeListTableColumns: TableColumn[] =  [
    ['id', 'ID', undefined],
    ['setupName', 'Trade Setup Name', undefined],
    ['security', 'Security', (a: IAutoTrade, b: IAutoTrade, currentDirection: "asc" | "desc") => {
      const valueA = `${a.ibkrOpenOrder?.orderSide} ${a.ibkrOpenOrder?.optionName}`;
      const valueB = `${b.ibkrOpenOrder?.orderSide} ${b.ibkrOpenOrder?.optionName}`;
      return TradeModel.compareHelper(valueA, valueB, currentDirection)
    }],
    ['positionTypeId', 'Position Type',undefined],
    ['openQuantity', 'Quantity', undefined],
    ['openPrice', 'Open Price', (a: IAutoTrade, b: IAutoTrade, currentDirection: "asc" | "desc") => {
      const valueA = `${a.ibkrOpenOrder?.orderPrice}`;
      const valueB = `${b.ibkrOpenOrder?.orderPrice}`;
      return TradeModel.compareHelper(valueA, valueB, currentDirection)
    }],
    ['closePrice', 'Close Price', (a: IAutoTrade, b: IAutoTrade, currentDirection: "asc" | "desc") => {
      const valueA = `${a.ibkrCloseOrder?.orderPrice}`;
      const valueB = `${b.ibkrCloseOrder?.orderPrice}`;
      return TradeModel.compareHelper(valueA, valueB, currentDirection)
    }],
    [undefined, 'Profit/Loss', (a: IAutoTrade, b: IAutoTrade, currentDirection: "asc" | "desc") => {
      const valueA = a.ibkrCloseOrder && a.ibkrOpenOrder ? (a.ibkrCloseOrder.orderPrice - a.ibkrOpenOrder.orderPrice) : null;
      const valueB = b.ibkrCloseOrder && b.ibkrOpenOrder ? (b.ibkrCloseOrder.orderPrice - b.ibkrOpenOrder.orderPrice) : null;
      return TradeModel.compareHelper(valueA, valueB, currentDirection)
    }],
    ['openAt', 'Opened On', undefined],
    [undefined, 'Trade', undefined],
    [undefined, 'Bid Price', undefined],
    [undefined, 'Ask Price', undefined],
    [undefined, 'Current Price', undefined],
    ['managementTypeId', 'Exit Mode', undefined],
    ['closeAt', 'Closed On', (a: IAutoTrade, b: IAutoTrade, currentDirection: "asc" | "desc") => {
      const valueA = `${a.closeAt || ''}`;
      const valueB = `${b.closeAt || ''}`;
      return TradeModel.compareHelper(valueA, valueB, currentDirection)
    }],
    ['statusReasonId', 'Trade Reason', undefined],
    ['closeTriggerTypeId', 'Exit Trigger', (a: IAutoTrade, b: IAutoTrade, currentDirection: "asc" | "desc") => {
      const valueA = `${TriggerExitTypeEnumHelper.names.get(a.closeTriggerTypeId) || ''}`;
      const valueB = `${TriggerExitTypeEnumHelper.names.get(b.closeTriggerTypeId) || ''}`;
      return TradeModel.compareHelper(valueA, valueB, currentDirection)
    }],
    ['statusId', 'Status', undefined]
  ].map((fields) => {
    const [name, title, comparator] = fields;
    return {
      name,
      title,
      comparator
    } as TableColumn
  });

  static getOpenedFilterFromPreset(presetDateRangeFilter: PresetDateRange | undefined) {
    if (!presetDateRangeFilter) {
      return({ startTime: undefined, endTime: undefined });
    }

    if (presetDateRangeFilter?.toString() === PresetDateRange.TODAY.toString()) {
      return({ startTime: StockHelper.fullHours().start.getTimeSec(), endTime: StockHelper.fullHours().end.getTimeSec() });
    }

    if (presetDateRangeFilter?.toString() === PresetDateRange.YESTERDAY.toString()) {
      const yesterday = StockHelper.findPreviousTradingDay(new Date());
      return({
        startTime: StockHelper.fullHours(yesterday).start.getTimeSec(),
        endTime: StockHelper.fullHours(yesterday).end.getTimeSec()
      });
    }

    if (presetDateRangeFilter?.toString() === PresetDateRange.THIS_WEEK.toString()) {
      const monday = Helper.getStartDateOfWeek(1, new Date());
      const friday = Helper.getStartDateOfWeek(5, new Date());
      return({ startTime: StockHelper.fullHours(monday).start.getTimeSec(), endTime: StockHelper.fullHours(friday).end.getTimeSec() });
    }

    if (presetDateRangeFilter?.toString() === PresetDateRange.LAST_WEEK.toString()) {
      const monday = Helper.getStartDateOfWeek(1, new Date(), 1);
      const friday = Helper.getStartDateOfWeek(5, new Date(), 1);
      return({ startTime: StockHelper.fullHours(monday).start.getTimeSec(), endTime: StockHelper.fullHours(friday).end.getTimeSec() });
    }

    if (presetDateRangeFilter?.toString() === PresetDateRange.THIS_MONTH.toString()) {
      const startOfMonth = Helper.getFirstOfMonth();
      const lastOfMonth = Helper.getLastDateOfMonth();
      return({ startTime: StockHelper.fullHours(startOfMonth).start.getTimeSec(), endTime: StockHelper.fullHours(lastOfMonth).end.getTimeSec() });
    }    

    if (presetDateRangeFilter?.toString() === PresetDateRange.LAST_30.toString()) {
      const today = new Date();
      const thiryDaysAgo = DateHelper.prevNextDateUs(today, -30);
      return({ startTime: StockHelper.fullHours(thiryDaysAgo).start.getTimeSec(), endTime: StockHelper.fullHours(today).end.getTimeSec() });
    }    


  }

  static signalRepeatType = new Map([
    [SignalRepeatType.PRIMARY, 'Primary'],
    [SignalRepeatType.REPEAT, 'Repeat'],
  ]);
  
  static signalDirectionType = new Map([
    [SignalDirectionType.UP, 'UP'],
    [SignalDirectionType.DOWN, 'DOWN'],
  ]);

  static oppositeDir = new Map([[0, 'No'], [1, 'Yes']]);

  static signalRepeatIntervalSecs = AnalysisFilters.SignalRepeatTypes;


  static calcProfitLossOption (open: number, close: number, quantity: number) {
    return (close - open) * quantity * 100;
  }
  
  static calcTradeProfitLossOption (trade: IAutoTrade) {
    if (trade.ibkrOpenOrder && trade.ibkrCloseOrder) {
      return this.calcProfitLossOption(trade.ibkrOpenOrder.orderPrice, trade.ibkrCloseOrder.orderPrice,  trade.closeQuantity);
    }
    return undefined;
  }

  static getOptionTypeFromDirectionTypes = (directionTypes: DirectionType[] | undefined) => {
    if (directionTypes && directionTypes.length === 1 && directionTypes[0] === DirectionType.DOWN) return OptionType.PUT;
    return OptionType.CALL;
  }

  static async createTradeSetupFromSignalAlert(signalAlertId: number) {
    const signalAlert: ISignalAlert = await adminSignalAlertApiService.getById(signalAlertId, true);
    const analysisSetup: Partial<AnalysisInputParams> = signalAlert.data;
    const tradeTemplate: ITradeTemplate = {
      maxExpirationDays: 9,
      minExpirationDays: 2,
      strikePriceOffset: 0,
      optionType: this.getOptionTypeFromDirectionTypes(signalAlert.data.openTriggerDirectionTypes),
      ibkr: {
        side: IBKRSide.BUY,
        orderType: IBKROrderType.MKT,
      }
    }
    const newTradeSetup: IAutoTradeSetup = {
      statusId: AutoTradeSetupStatusEnum.PAUSED,
      tradeSecurityTypeId: AutoTradeSecurityTypeEnum.OPTION,
      name: `${signalAlert.id} ${signalAlert.name}`,
      maxActiveTrades: 2,
      quantityPerTrade: 1,
      tradeTemplate: tradeTemplate,
      analysisSetup: analysisSetup
    } as IAutoTradeSetup;

    return await adminAutoTradeSetupService.create(newTradeSetup);
  }

  static getDefaultOptionType (setupOptions: Partial<TradeSetupFilterOptions>) : OptionType | undefined {
    const openTriggerDirectionTypes = setupOptions.openTriggerDirectionTypes && setupOptions.openTriggerDirectionTypes.length > 0 ? setupOptions.openTriggerDirectionTypes[0] : undefined;
    if (openTriggerDirectionTypes === DirectionType.DOWN) { 
      return OptionType.PUT;
    }

    const openSignalSetup = setupOptions.openSignalSetup;
    const openSignalDirectionType = openSignalSetup?.signals && openSignalSetup.signals && openSignalSetup.signals[0].directionTypes[0];

    if (openSignalDirectionType === SignalDirectionType.DOWN) {
      return OptionType.PUT;
    }

    return OptionType.CALL;
  }

  static getOpenPriceWeightedAverage (trades: IAutoTrade[]) {
    const result = trades.reduce(
      (acc, trade, index, array) => {
        if (trade.ibkrOpenOrder ) {
          acc.totalWeightedValue += trade.ibkrOpenOrder.orderPrice * trade.openQuantity;
          acc.totalWeight += trade.openQuantity;
        }
        return acc;
      },
      {totalWeightedValue: 0, totalWeight: 0})

    return result.totalWeightedValue / result.totalWeight;
  }

  static getClosePriceWeightedAverage (trades: IAutoTrade[]) {
    const result = trades.reduce(
      (acc, trade, index, array) => {
        if (trade.ibkrCloseOrder) {
          acc.totalWeightedValue += trade.ibkrCloseOrder.orderPrice * trade.closeQuantity;
          acc.totalWeight += trade.closeQuantity;
        }
        return acc;
      },
      {totalWeightedValue: 0, totalWeight: 0})

    return result.totalWeightedValue / result.totalWeight;
  }

  static getTradesProfitLoss (trades: IAutoTrade[]) {
    return trades.reduce((total, trade) => { 
      const {profitLoss, initialValue, finalValue} = total;
      if (!trade.ibkrCloseOrder || !trade.ibkrOpenOrder) {
        return {
          profitLoss,
          initialValue,
          finalValue
        }
      }
      return {
        profitLoss: profitLoss + this.calcProfitLossOption(trade.ibkrOpenOrder.orderPrice, trade.ibkrCloseOrder.orderPrice, trade.closeQuantity),
        initialValue: initialValue + (trade.ibkrOpenOrder.orderPrice * trade.closeQuantity),
        finalValue: finalValue + (trade.ibkrCloseOrder.orderPrice * trade.closeQuantity)
      }
    }, {
      profitLoss: 0,
      initialValue: 0,
      finalValue: 0
    });
  }

  static getProfitLossPct (initialValue: number, finalValue: number) {
    return (finalValue - initialValue) / initialValue * 100;
  }

  static getTradesTotalQuantity(trades: IAutoTrade[]) {
    return trades.reduce((acc: IQuantity, trade) => {
      switch (trade.statusId) {
        case AutoTradeStatusEnum.ACTIVE:
          acc.active +=1;
          break;
        case AutoTradeStatusEnum.CLOSED:
          acc.closed += 1;
          break;
        default:
          acc.other += 1;
      }
      acc.total +=1;
      return acc;
    }, {
      active: 0,
      closed: 0,
      total: 0,
      other: 0,
    } as IQuantity);
  }

  static getSecurityName (ibkrOpenOrder: IAutoTradeIbkrOrder | undefined) {
    if (!ibkrOpenOrder) {
      return undefined;
    }
    return `${ibkrOpenOrder.orderSide} ${ibkrOpenOrder.optionName}`;
  }
  
  static calcUnrealizedProfitLossOption (trade: IAutoTrade, currentPrice: number) {
    if (trade.ibkrOpenOrder) {
      return TradeModel.calcProfitLossOption(trade.ibkrOpenOrder.orderPrice, currentPrice, trade.openQuantity);
    }
    return undefined;
  }

  static getUrealizedProfitLossOptionTrades (trades: IAutoTrade[], livePrices: ITradeLivePrice[]) {
    const unrealizedPl = trades
      .filter(t=>t.statusId===AutoTradeStatusEnum.ACTIVE)
      .filter(t=>livePrices.find((p)=>p.id === t.id ))
      .reduce((pl, trade)=> {
        const p = livePrices.find((p)=>p.id===trade.id);
        if (p) {
          return pl += TradeModel.calcUnrealizedProfitLossOption(trade, p.lastPrice) || 0
        }
        return pl;
      }, 0);
    return unrealizedPl;
  }

  static getClosedShares (trades:IAutoTrade[]) {
    return trades.reduce((t, trade)=> t+= (trade.statusId === AutoTradeStatusEnum.CLOSED ? trade.closeQuantity * 100 : 0), 0);
  }

  static getActiveShares (trades:IAutoTrade[]) {
    return trades.reduce((t, trade)=> t+= (trade.statusId === AutoTradeStatusEnum.ACTIVE ? trade.openQuantity * 100 : 0), 0);
  }

  static getActiveTotalCost (trades:IAutoTrade[]) {
    return trades.reduce((t, trade)=> t+= (trade.statusId === AutoTradeStatusEnum.ACTIVE ? 
      (trade.ibkrOpenOrder ? trade.ibkrOpenOrder.orderPrice * trade.openQuantity * 100 : 0) 
      : 0), 0);
  }

  static getMaxTotalInvested (trades:IAutoTrade[]) {
    return trades.reduce((t, trade)=> t+= ([AutoTradeStatusEnum.ACTIVE, AutoTradeStatusEnum.CLOSED].includes(trade.statusId) ? 
      (trade.ibkrOpenOrder ? trade.ibkrOpenOrder.orderPrice * trade.openQuantity * 100 : 0) 
      : 0), 0);
  }
}